import { Model } from 'app/model/backendmodel';
import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { Metamodel } from 'app/model/base/model';
import { XmlFormatReadSettings } from 'app/model/pipeline/copysource/format-read-settings/xml-format-read-settings';
import { FormatReadSettingsAdapter, formatReadSettingsAdapts } from './format-read-settings.adapter';
import { ValueAdapter } from 'app/model/adapters/value.adapter';
import { CopySource } from 'app/model/pipeline/copysource/copysource';
import { isLogicAppExpression, isExpressionObject } from 'app/shared/utils/expression.util';
import {
    convertCompressionSettingsToModel,
    convertCompressionSettingsToResource
} from '../compression-read-settings/compression-read-settings.util';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(XmlFormatReadSettings),
    asynchronous: true,
    adapts: {
        ...formatReadSettingsAdapts,
        validationMode: '*',
        namespaces: '*',
        namespacePrefixes: '*',
        detectDataType: '*'
    }
})
export class XmlFormatReadSettingsAdapter extends FormatReadSettingsAdapter<
    XmlFormatReadSettings,
    Model.XmlFormatReadSettings
> {
    protected override async _toResource(model: XmlFormatReadSettings): Promise<Model.XmlFormatReadSettings> {
        const result: Model.XmlFormatReadSettings = FormatReadSettingsAdapter.getCommonReadSettingsProperties(
            Metamodel.modelFor(XmlFormatReadSettings).name
        );
        result.validationMode = ValueAdapter.toResource(model, XmlFormatReadSettings.VALIDATION_MODE);
        result.detectDataType = ValueAdapter.toResource(model, XmlFormatReadSettings.DETECT_DATA_TYPE);
        result.namespaces = ValueAdapter.toResource(model, XmlFormatReadSettings.NAMESPACES);
        if (result.namespaces) {
            if (XmlFormatReadSettings.NAMESPACE_PREFIXES.templateValue(model)) {
                result.namespacePrefixes = ValueAdapter.toResource(model, XmlFormatReadSettings.NAMESPACE_PREFIXES);
            } else {
                result.namespacePrefixes = {};
                (model.namespacePrefixes || [])
                    .filter((namespacePrefix) => namespacePrefix.key && namespacePrefix.val)
                    .forEach(
                        (namespacePrefix) => (result.namespacePrefixes[namespacePrefix.key] = namespacePrefix.val)
                    );
                if (Object.keys(result.namespacePrefixes).length === 0) {
                    result.namespacePrefixes = undefined;
                }
            }
        }
        await convertCompressionSettingsToResource(model.compressionProperties, result, this.context);
        return result;
    }

    protected override async _toModel(
        resource: Model.XmlFormatReadSettings,
        copySource: CopySource
    ): Promise<XmlFormatReadSettings> {
        const result = FormatReadSettingsAdapter.createReadSettingsWithCommonProperties(
            XmlFormatReadSettings.MODEL,
            copySource
        ) as XmlFormatReadSettings;
        result.namespaces = undefined;
        ValueAdapter.toModel(result, XmlFormatReadSettings.VALIDATION_MODE, resource.validationMode);
        ValueAdapter.toModel(result, XmlFormatReadSettings.DETECT_DATA_TYPE, resource.detectDataType);
        ValueAdapter.toModel(result, XmlFormatReadSettings.NAMESPACES, resource.namespaces);

        if (resource.namespacePrefixes) {
            if (
                (typeof resource.namespacePrefixes === 'string' && isLogicAppExpression(resource.namespacePrefixes)) ||
                (typeof resource.namespacePrefixes === 'object' && isExpressionObject(resource.namespacePrefixes))
            ) {
                ValueAdapter.toModel(result, XmlFormatReadSettings.NAMESPACE_PREFIXES, resource.namespacePrefixes);
            } else {
                result.namespacePrefixes = [];
                Object.keys(resource.namespacePrefixes).forEach((key) =>
                    result.namespacePrefixes.push({
                        key,
                        val: resource.namespacePrefixes[key]
                    })
                );
            }
            // eslint-disable-next-line eqeqeq
            if (resource.namespaces == undefined) {
                result.namespaces = true;
            }
        }
        await convertCompressionSettingsToModel(resource.compressionProperties, result, this.context);
        return result;
    }
}
